import React, { useState } from 'react';
import { 
    Paper, TableRow, TableCell, Checkbox, Chip, Typography, Button,
    Box, FormControl, Select, MenuItem, Autocomplete, TextField, Tooltip, IconButton, FormControlLabel, Stack, Divider
} from '@mui/material';
import { 
    Sort as SortIcon, DoneAll as DoneAllIcon, RemoveDone as RemoveDoneIcon, MailOutline as MailOutlineIcon 
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, addDays } from 'date-fns';
import DataTable from '../DataTable';
import CustomDatePicker from '../CustomDatePicker';
import { formatDateDisplay } from '../../utils/dateUtils';
import ConfirmDialog from '../ConfirmDialog';
import ExtraOrderDialog from './ExtraOrderDialog';
import * as API from '../../api'; 

// --- HẰNG SỐ ---
const EXTRA_OPS_LABELS = { 'OPEN': 'Chờ ĐH dịch vụ', 'CONTACTED': 'Đã báo NCC', 'CONFIRMED': 'Đã xác nhận', 'DONE': 'Hoàn tất' };
const EXTRA_OPS_COLORS = { 'OPEN': 'error', 'CONTACTED': 'warning', 'CONFIRMED': 'info', 'DONE': 'success' };
const DEFAULT_EXTRA_STATUS = ['OPEN', 'CONTACTED', 'CONFIRMED']; 

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

const ExtraOpsTab = ({ bookings, handleViewBooking, onReload, showToast }) => {
    
    // --- 1. STATE BỘ LỌC (GIAO DIỆN) ---
    const [quickFilterMode, setQuickFilterMode] = useState('CUSTOM');
    const [filterStartDate, setFilterStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [filterEndDate, setFilterEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [isAllTime, setIsAllTime] = useState(false);
    const [filterOpsStatus, setFilterOpsStatus] = useState(DEFAULT_EXTRA_STATUS);
    const [filterExtraSupplier, setFilterExtraSupplier] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');

    // --- 2. STATE ACTIVE FILTER (ÁP DỤNG LỌC) ---
    const [activeFilters, setActiveFilters] = useState({
        startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        opsStatus: DEFAULT_EXTRA_STATUS,
        allTime: false,
        sort: 'asc',
        extraSupplier: null
    });

    // --- 3. STATE THAO TÁC HÀNG LOẠT & DIALOG ---
    const [selectedIds, setSelectedIds] = useState([]); 
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', content: '', action: null, confirmColor: 'primary' });

    const [isExtraOrderOpen, setIsExtraOrderOpen] = useState(false);
    const [selectedExtrasForOrder, setSelectedExtrasForOrder] = useState([]);

    // --- HÀM XỬ LÝ SỰ KIỆN GIAO DIỆN BỘ LỌC ---
    const handleQuickFilterChange = (event) => {
        const mode = event.target.value;
        setQuickFilterMode(mode);
        if (mode === 'CUSTOM') return;
        const today = new Date(); let start, end;
        if (mode === 'TODAY') { start = format(today, 'yyyy-MM-dd'); end = format(today, 'yyyy-MM-dd'); }
        else if (mode === 'TOMORROW') { const tmr = addDays(today, 1); start = format(tmr, 'yyyy-MM-dd'); end = format(tmr, 'yyyy-MM-dd'); }
        else if (mode === 'NEXT_3_DAYS') { start = format(today, 'yyyy-MM-dd'); end = format(addDays(today, 3), 'yyyy-MM-dd'); }
        setFilterStartDate(start); setFilterEndDate(end); setIsAllTime(false);
    };

    const handleApplyFilter = () => {
        setActiveFilters({
            startDate: filterStartDate, endDate: filterEndDate, opsStatus: filterOpsStatus,
            allTime: isAllTime, sort: sortOrder, extraSupplier: filterExtraSupplier
        });
        setSelectedIds([]); 
    };

    const toggleSort = () => {
        const newSort = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(newSort);
        setActiveFilters(prev => ({ ...prev, sort: newSort }));
    };

    const handleToggleAllOpsStatus = () => {
        const allKeys = Object.keys(EXTRA_OPS_LABELS);
        if (filterOpsStatus.length === allKeys.length) setFilterOpsStatus([]);
        else setFilterOpsStatus(allKeys);
    };

    const getExtraSupplierOptions = () => {
        const suppliersMap = new Map();
        bookings.forEach(bk => {
            if (bk.extras) {
                bk.extras.forEach(e => {
                    if (e.payment_method === 'SEPARATE' && e.supplier) {
                        const id = typeof e.supplier === 'object' ? e.supplier.id : e.supplier;
                        const name = e.supplier_name || 'Nhà cung cấp dịch vụ';
                        if (!suppliersMap.has(id)) suppliersMap.set(id, { id, name });
                    }
                });
            }
        });
        return Array.from(suppliersMap.values());
    };

    // --- LOGIC LỌC & TRẢI PHẲNG DỮ LIỆU ---
    const getFilteredExtras = () => {
        let allExtras = [];
        bookings.forEach(bk => {
            if (bk.extras && bk.extras.length > 0) {
                bk.extras.forEach(extra => {
                    if (extra.payment_method === 'SEPARATE') {
                        allExtras.push({
                            ...extra,
                            bk_code: bk.booking_code,
                            customer_detail: bk.customer_detail
                        });
                    }
                });
            }
        });

        return allExtras.filter(extra => {
            const isMissingDate = !extra.service_date;

            if (!activeFilters.allTime && !isMissingDate) {
                const dateToCheck = extra.service_date.split('T')[0];
                if (dateToCheck < activeFilters.startDate || dateToCheck > activeFilters.endDate) return false;
            }

            const opsStt = extra.operation_status || 'OPEN';
            if (activeFilters.opsStatus.length > 0 && !activeFilters.opsStatus.includes(opsStt)) return false;

            if (activeFilters.extraSupplier) {
                const sId = typeof extra.supplier === 'object' ? extra.supplier?.id : extra.supplier;
                if (sId !== activeFilters.extraSupplier.id) return false;
            }

            return true;
        }).sort((a, b) => {
            const noDateA = !a.service_date;
            const noDateB = !b.service_date;
            if (noDateA && !noDateB) return -1;
            if (!noDateA && noDateB) return 1;

            const timeA = a.service_date ? new Date(a.service_date).getTime() : 0;
            const timeB = b.service_date ? new Date(b.service_date).getTime() : 0;
            return activeFilters.sort === 'asc' ? timeA - timeB : timeB - timeA;
        });
    };

    const displayData = getFilteredExtras();

    // --- THAO TÁC BẢNG & HÀNG LOẠT ---
    const handleSelectRow = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedIds(displayData.map(row => row.id));
        else setSelectedIds([]);
    };

    const closeConfirmModal = () => setConfirmModal({ ...confirmModal, open: false });
    const handleConfirmAction = () => {
        if (confirmModal.action) confirmModal.action();
        closeConfirmModal();
    };

    const executeBulkUpdate = async (newStatus) => {
        try {
            const updatePromises = selectedIds.map(id => 
                API.client.patch(`/booking-extras/${id}/update_status/`, { operation_status: newStatus })
            );
            await Promise.all(updatePromises);

            if (showToast) showToast("Cập nhật trạng thái Dịch vụ thành công!", "success");
            setSelectedIds([]); 
            if (onReload) onReload(); 
        } catch (error) {
            console.error("Lỗi cập nhật:", error);
            if (showToast) showToast("Có lỗi xảy ra khi cập nhật trạng thái.", "error");
        }
    };

    const handleBulkUpdateStatus = (newStatus) => {
        setConfirmModal({
            open: true,
            title: 'Xác nhận cập nhật',
            content: `Cập nhật trạng thái ${EXTRA_OPS_LABELS[newStatus]} cho ${selectedIds.length} dịch vụ đang chọn?`,
            confirmColor: EXTRA_OPS_COLORS[newStatus] || 'primary',
            action: () => executeBulkUpdate(newStatus)
        });
    };

    const handleOpenBulkExtraOrder = () => {
        const selectedObjs = displayData.filter(e => selectedIds.includes(e.id));
        const firstSupplier = selectedObjs[0]?.supplier;
        
        const isSameSupplier = selectedObjs.every(e => {
            const s1 = typeof e.supplier === 'object' ? e.supplier?.id : e.supplier;
            const s2 = typeof firstSupplier === 'object' ? firstSupplier?.id : firstSupplier;
            return s1 === s2;
        });

        if (!isSameSupplier) {
            if(showToast) showToast("Lỗi: Không thể gửi lệnh gộp cho NCC KHÁC NHAU!", "error");
            return;
        }
        if (!firstSupplier) {
            if(showToast) showToast("Lỗi: Dịch vụ được chọn chưa được gán Nhà cung cấp!", "error");
            return;
        }
        
        setSelectedExtrasForOrder(selectedObjs);
        setIsExtraOrderOpen(true);
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            
            {/* === KHU VỰC BỘ LỌC === */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', mb: 2 }}>
                    <FormControl size="small" sx={{ width: '160px', pt: 3.2 }}>
                        <Select value={quickFilterMode} onChange={handleQuickFilterChange} displayEmpty>
                            <MenuItem value="CUSTOM">-- Lọc nhanh --</MenuItem>
                            <MenuItem value="TODAY">Hôm nay</MenuItem>
                            <MenuItem value="TOMORROW">Ngày mai</MenuItem>
                            <MenuItem value="NEXT_3_DAYS">3 ngày tới</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box style={{ width: '160px' }}>
                            <CustomLabel title="Từ ngày SD" />
                            <CustomDatePicker value={filterStartDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterStartDate(e.target.value)} />
                        </Box>
                        <Box style={{ width: '160px' }}>
                            <CustomLabel title="Đến ngày SD" />
                            <CustomDatePicker value={filterEndDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterEndDate(e.target.value)} />
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Lọc theo Nhà cung cấp" />
                        <Autocomplete 
                            size="small" fullWidth 
                            options={getExtraSupplierOptions()} getOptionLabel={(option) => option.name || ''} 
                            value={filterExtraSupplier} onChange={(e, newValue) => setFilterExtraSupplier(newValue)} 
                            renderInput={(params) => <TextField {...params} placeholder="Chọn nhà cung cấp..." />} 
                        />
                    </Box>
                    <Box sx={{ pt: 3.2 }}>
                        <Tooltip title={`Sắp xếp ngày sử dụng: ${sortOrder === 'asc' ? 'Tăng dần' : 'Giảm dần'}`}>
                            <IconButton onClick={toggleSort} sx={{ border: '1px solid #ced4da', borderRadius: 1, height: '40px', color: '#007373', bgcolor: 'white', marginBottom: '12px' }}>
                                <SortIcon sx={{ transform: sortOrder === 'desc' ? 'scaleY(-1)' : 'none' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ pt: 3.2 }}>
                        <Button variant="contained" onClick={handleApplyFilter} sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, height: '40px', px: 3, borderRadius: 2, whiteSpace: 'nowrap', marginBottom: '12px' }}>
                            Áp dụng bộ lọc
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box>
                        <FormControlLabel 
                            control={<Checkbox checked={isAllTime} onChange={(e) => { setIsAllTime(e.target.checked); if(e.target.checked) setQuickFilterMode('CUSTOM'); }} sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />} 
                            label={<Typography variant="body2" fontWeight="bold">Toàn thời gian</Typography>} 
                            sx={{ m: 0, mt: 2.5 }} 
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: '300px', borderLeft: '1px solid #eee', pl: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CustomLabel title="Trạng thái ĐH Dịch vụ" />
                            <Tooltip title={filterOpsStatus.length === Object.keys(EXTRA_OPS_LABELS).length ? "Bỏ chọn tất cả" : "Chọn tất cả"}>
                                <IconButton size="small" onClick={handleToggleAllOpsStatus} sx={{ padding: 0, color: '#007373' }}>
                                    {filterOpsStatus.length === Object.keys(EXTRA_OPS_LABELS).length ? <RemoveDoneIcon fontSize="small" /> : <DoneAllIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {Object.keys(EXTRA_OPS_LABELS).map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={<Checkbox checked={filterOpsStatus.indexOf(key) > -1} onChange={(e) => setFilterOpsStatus(prev => e.target.checked ? [...prev, key] : prev.filter(k => k !== key))} size="small" sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />}
                                    label={<Typography variant="body2">{EXTRA_OPS_LABELS[key]}</Typography>}
                                    sx={{ mr: 0, ml: 0 }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* === THANH BULK ACTION === */}
            {selectedIds.length > 0 && (
                <Paper sx={{ p: 1, mb: 2, bgcolor: '#e3f2fd', border: '1px solid #1976d2', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 2, color: '#1976d2' }}>
                        Đang chọn: {selectedIds.length} dịch vụ
                    </Typography>
                    
                    <Button 
                        variant="contained" size="small" color="primary" 
                        onClick={handleOpenBulkExtraOrder} startIcon={<MailOutlineIcon />}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Tạo lệnh đặt dịch vụ / Gửi Mail
                    </Button>

                    <Divider orientation="vertical" flexItem />

                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Cập nhật trạng thái ĐH:</Typography>
                    <Stack direction="row" spacing={1}>
                        {['CONTACTED', 'CONFIRMED', 'DONE'].map(status => (
                            <Button 
                                key={status} size="small" variant="contained" 
                                sx={{ 
                                    bgcolor: 'white', color: theme => theme.palette[EXTRA_OPS_COLORS[status]].main,
                                    border: '1px solid', borderColor: theme => theme.palette[EXTRA_OPS_COLORS[status]].main,
                                    '&:hover': { bgcolor: theme => theme.palette[EXTRA_OPS_COLORS[status]].main, color: 'white' }
                                }}
                                onClick={() => handleBulkUpdateStatus(status)} 
                            >
                                {EXTRA_OPS_LABELS[status]}
                            </Button>
                        ))}
                    </Stack>
                </Paper>
            )}

            {/* === BẢNG DỮ LIỆU === */}
            <DataTable
                maxHeight="65vh"
                columns={[
                    {
                        label: (
                            <Checkbox color="primary" sx={{ p: 0, color: 'white' }}
                                indeterminate={selectedIds.length > 0 && selectedIds.length < displayData.length}
                                checked={displayData.length > 0 && selectedIds.length === displayData.length}
                                onChange={handleSelectAll}
                            />
                        ),
                        minWidth: 50, align: 'center'
                    },
                    { label: 'Mã BK', minWidth: 100 },
                    { label: 'Khách hàng', minWidth: 150 },
                    { label: 'Nhà cung cấp (NCC)', minWidth: 200 },
                    { label: 'Tên dịch vụ', minWidth: 180 },
                    { label: 'SL', minWidth: 60, align: 'center' },
                    { label: 'Ngày SD', minWidth: 180 },
                    { label: 'Trạng thái ĐH', minWidth: 120 },
                    { label: 'Thao tác', align: 'center', minWidth: 100 },
                ]}
                data={displayData}
                renderRow={(row) => (
                    <TableRow key={row.id} hover>
                        <TableCell padding="checkbox" align="center">
                            <Checkbox color="primary" checked={selectedIds.includes(row.id)} onChange={(e) => { e.stopPropagation(); handleSelectRow(row.id); }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#007373' }}>{row.bk_code}</TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold">{row.customer_detail?.name || 'Khách lẻ'}</Typography>
                            <Typography variant="caption" color="textSecondary">{row.customer_detail?.phone || ''}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                                {row.supplier_name || 'Chưa gán Nhà cung cấp'}
                            </Typography>
                        </TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">{row.quantity}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {row.service_date ? formatDateDisplay(row.service_date) : '---'}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip 
                                label={EXTRA_OPS_LABELS[row.operation_status || 'OPEN']} 
                                color={EXTRA_OPS_COLORS[row.operation_status || 'OPEN']} 
                                size="small"
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Button size="small" variant="outlined" color="warning" sx={{ fontSize: '0.7rem', py: 0.5, textTransform: 'none', bgcolor: 'white' }} onClick={() => handleViewBooking(row.bk_code)}>
                                Xem BK
                            </Button>
                        </TableCell>
                    </TableRow>
                )}
            />
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#007373' }}>
                    Tổng: {displayData.length} dịch vụ phát sinh lẻ
                </Typography>
            </Box>

            {/* Dialog xác nhận trạng thái */}
            <ConfirmDialog 
                open={confirmModal.open}
                title={confirmModal.title}
                content={confirmModal.content}
                onClose={closeConfirmModal}
                onConfirm={handleConfirmAction}
                confirmColor={confirmModal.confirmColor}
            />

            {/* --- DIALOG LỆNH ĐẶT DỊCH VỤ --- */}
            <ExtraOrderDialog 
                open={isExtraOrderOpen}
                onClose={() => setIsExtraOrderOpen(false)}
                selectedExtras={selectedExtrasForOrder}
                showToast={showToast}
                onSuccess={() => { setSelectedIds([]); if(onReload) onReload(); }}
            />
        </Paper>
    );
};
export default ExtraOpsTab;